import { useMemo } from 'react'
import { useText, type FooterProps, type NavEntry } from '@kkf/design'
import { BOTTOM_NAV, FOOTER, HEADER_CTA, NAV, SIDEBAR } from '../data/shell'
import { NOT_FOUND } from '../data/not-found'
import { useT } from '../i18n'

/**
 * The shell's content, in the reader's language.
 *
 * `data/shell.ts` holds catalogue **keys** for its labels and `Text` for its prose;
 * `@kkf/design`'s `Header`, `Footer` and `MobileSidebar` take plain strings, because the
 * design system renders what it is handed and must stay usable by an app with no locale
 * provider above it. This is the seam between the two.
 *
 * One per app rather than one shared: the shapes differ — this app's recent-posts column
 * is derived from real blog posts, the garage's is two invitations, Partners has an
 * install promo — and ground rule 5 promotes a block when a *second* app needs the same
 * one, not when two apps need something of the same kind.
 *
 * Memoised on the two lookups, because `AppShell` memoises its route lookup on object
 * identity and a fresh object every render would defeat it.
 */
export function useShellContent() {
  const t = useT()
  const s = useText()

  return useMemo(() => {
    /** `{ labelKey, to?, href? }` becomes the `{ label, to?, href? }` a link wants. */
    const link = <T extends { labelKey: Parameters<typeof t>[0] }>(entry: T) => {
      const { labelKey, ...rest } = entry
      return { label: t(labelKey), ...rest }
    }

    return {
      nav: NAV.map(link) as NavEntry[],
      headerCta: link(HEADER_CTA),
      footer: {
        preamble: {
          subTitle: s(FOOTER.preamble.subTitle),
          title: s(FOOTER.preamble.title),
          paragraphs: FOOTER.preamble.paragraphs.map(s),
          image: FOOTER.preamble.image,
          imageAlt: s(FOOTER.preamble.imageAlt),
        },
        wayfinding: {
          label: t(FOOTER.wayfinding.labelKey),
          links: FOOTER.wayfinding.links.map(link),
        },
        logo: FOOTER.logo,
        homeTo: FOOTER.homeTo,
        about: s(FOOTER.about),
        socials: FOOTER.socials,
        quickLinks: {
          title: t(FOOTER.quickLinks.titleKey),
          links: FOOTER.quickLinks.links.map(link),
        },
        // The posts themselves are the blog's, in whatever language they were written —
        // only the column's heading is translated. See `data/shell.ts`.
        recentPosts: {
          title: t(FOOTER.recentPosts.titleKey),
          posts: FOOTER.recentPosts.posts,
        },
        contact: {
          title: t(FOOTER.contact.titleKey),
          // An inbox is printed as written; a fact about where we are comes from the
          // catalogue. `href` only ever belongs to the first kind.
          links: FOOTER.contact.links.map((entry) =>
            entry.labelKey
              ? { icon: entry.icon, label: t(entry.labelKey) }
              : { icon: entry.icon, label: entry.label ?? '', href: entry.href },
          ),
          newsletterPlaceholder: t(FOOTER.contact.newsletterPlaceholderKey),
          agreementText: t(FOOTER.contact.agreementTextKey),
          privacyPolicy: link(FOOTER.contact.privacyPolicy),
        },
        copyrightName: t(FOOTER.copyrightNameKey),
        bottomNav: FOOTER.bottomNav.map(link),
        vectorImage: FOOTER.vectorImage,
      } as Omit<FooterProps, 'onSubscribe' | 'tone'>,
      /*
       * The bottom tab bar. `menuLabel` is what appends the fifth tab — `AppShell` owns
       * the off-canvas's open state, so the app cannot build that one itself.
       */
      bottomNav: {
        label: t('shell.bottomNav'),
        menuLabel: t('tab.menu'),
        items: BOTTOM_NAV.map(({ labelKey, ...rest }) => ({ label: t(labelKey), ...rest })),
      },
      sidebar: {
        followTitle: t(SIDEBAR.followTitleKey),
        socials: SIDEBAR.socials,
        sitesTitle: t(SIDEBAR.sitesTitleKey),
        sites: SIDEBAR.sites.map(link),
      },
      notFound: {
        logo: NOT_FOUND.logo,
        name: NOT_FOUND.name,
        code: NOT_FOUND.code,
        title: s(NOT_FOUND.title),
        descr: s(NOT_FOUND.descr),
        verse: {
          text: s(NOT_FOUND.verse.text),
          reference: s(NOT_FOUND.verse.reference),
          translation: NOT_FOUND.verse.translation,
        },
        primaryCta: link(NOT_FOUND.primaryCta),
        secondaryCta: link(NOT_FOUND.secondaryCta),
        linksLabel: t(NOT_FOUND.linksLabelKey),
        links: NOT_FOUND.links.map(link),
      },
    }
  }, [t, s])
}
